// Market Intelligence report configuration.
//
// This whole page is a BESPOKE Robb & Stucky report: fixed store markets,
// R&S campaign-segment naming (`RobbStucky_C1_Sarasota`), Broward County
// fallback zips, Wellen Park zip-scoping. None of it generalizes to other
// tenants, so it is gated to the R&S tenant. For any OTHER tenant the market
// list is EMPTY — the page renders a clean empty dashboard instead of
// leaking R&S markets/data across tenants.
//
// Generalizing to other tenants means deriving markets from the tenant's own
// locations (MI.getLocations) plus a per-tenant report config
// (a /v1/account/config endpoint or similar) — a follow-up, not done here.
import account from '../../data/account';
const RS_TENANT = 'anbsezhvanaj';
function currentTenantId() {
  try {
    return account.get() ? account.get().tenantId : null;
  } catch (e) {
    return null;
  }
}

// Only the R&S tenant gets the hardcoded R&S report structure. Evaluated at
// module load; tenant only changes via a full page reload (login), so this
// stays consistent with the signed-in account.
const isRobbStucky = currentTenantId() === RS_TENANT;

// Order matters for rendering (Outlet sits under Fort Myers; Wellen Park
// under Sarasota).
const RS_MARKETS = ['Fort Myers', 'Outlet', 'Sarasota', 'Wellen Park', 'Naples', 'Boca Raton', 'Broward County'];
export const MARKETS = isRobbStucky ? RS_MARKETS : [];

// Non-R&S tenants (2026-07-14): markets are DERIVED at runtime from the
// tenant's own MI locations (MI.getLocations) instead of this static list —
// the generic report (place/zip/product blocks per market, campaign tables
// when campaigns exist) renders over real data. R&S keeps the bespoke
// static structure above untouched; all R&S-specific decorations
// (segments, fallback zips, zip-scoping) stay empty for derived tenants.
export const DERIVE_MARKETS_FROM_LOCATIONS = !isRobbStucky;

// Markets that don't have a per-market website-attribution section
// (campaign data only). Rendered without place/zip/product blocks.
export const CAMPAIGN_ONLY_MARKETS = new Set(isRobbStucky ? ['Outlet'] : []);

// Zip-scoped market: campaigns sourced from a different system (Google
// Ads zip-targeted reports). Not available in the React app today, so
// the Wellen Park campaign table will be empty; the website-attribution
// blocks still render via the falback-zip path.
export const ZIP_SCOPED_MARKET = isRobbStucky ? 'Wellen Park' : null;
export const ZIP_SCOPED_POSTAL = isRobbStucky ? '34293' : null;

// Markets that get the C1/C2/C3 segment breakout (Social only).
export const SEGMENT_MARKETS = new Set(isRobbStucky ? ['Fort Myers', 'Sarasota', 'Naples', 'Boca Raton'] : []);

// Segment-campaign name pattern (e.g. `RobbStucky_C1_Sarasota`).
export const SEGMENT_PATTERN = /_C[123]_/;

// Video-only types — excluded from CTR math (no clickable inventory).
export const NON_CTR_TYPES = new Set(['YouTube', 'CTV/OTT']);

// Render order for campaign type buckets. Anything not in the list
// sorts last (idx 99).
export const CAMPAIGN_TYPE_ORDER = ['Display', 'Retarget', 'YouTube', 'Search', 'Social', 'CTV/OTT'];

// Fallback zips for markets not in the tenant's location_geo_target
// table (Wellen Park, Broward County).
export const FALLBACK_ZIPS = isRobbStucky ? {
  'Wellen Park': ['34293'],
  'Broward County': ['33019', '33028', '33029', '33067', '33071', '33301', '33306', '33316', '33323', '33325', '33326', '33327', '33328', '33330', '33331', '33332']
} : {};

// Markets whose city name doesn't match a city in the geo data, so the
// city-resolved zip table is skipped.
export const NO_CITY_RESOLVED_TABLE = new Set(isRobbStucky ? ['Broward County', 'Wellen Park'] : []);

// "All Markets" sentinel value used by the Location dropdown.
export const ALL_LOCATION_KEY = 'All';
export default {
  MARKETS,
  CAMPAIGN_ONLY_MARKETS,
  ZIP_SCOPED_MARKET,
  ZIP_SCOPED_POSTAL,
  SEGMENT_MARKETS,
  SEGMENT_PATTERN,
  NON_CTR_TYPES,
  CAMPAIGN_TYPE_ORDER,
  FALLBACK_ZIPS,
  NO_CITY_RESOLVED_TABLE,
  ALL_LOCATION_KEY
};